// Generic, bounded plain-text rendering of summary JSON (spec §5; product law:
// never enumerate LLM JSON fields). Output is text for a text node, never
// markup. Same shape as apps/api/src/archive/render.ts, plus depth, item and
// size limits because the browser renders whatever the api returns.
import type { Json } from '@kima-hub/contracts';
import { SUMMARY_RENDER_MAX_CHARS } from '@kima-hub/contracts';
import { COPY } from '../copy.js';

export interface RenderLimits {
  maxDepth: number;
  maxChars: number;
  maxItems: number;
}

export const DEFAULT_LIMITS: RenderLimits = { maxDepth: 8, maxChars: SUMMARY_RENDER_MAX_CHARS, maxItems: 200 };

// Control characters except tab and newline; bidi overrides and isolates that can disguise text.
// eslint-disable-next-line no-control-regex
const UNSAFE = /[\u0000-\u0008\u000B-\u001F\u007F‪-‮⁦-⁩]/g;

function clean(text: string): string {
  return text.replace(UNSAFE, '');
}

function label(key: string): string {
  return clean(key).replace(/_/g, ' ');
}

function scalar(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return clean(value);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

function isContainer(value: unknown): value is object {
  return typeof value === 'object' && value !== null;
}

export function renderJsonText(value: Json | unknown, limits: Partial<RenderLimits> = {}): string {
  const { maxDepth, maxChars, maxItems } = { ...DEFAULT_LIMITS, ...limits };
  const lines: string[] = [];
  let size = 0;
  let truncated = false;
  const seen = new WeakSet<object>();

  const push = (line: string): boolean => {
    if (truncated) return false;
    const room = maxChars - size;
    if (line.length > room) {
      // Keep what fits of the line that crosses the limit, then stop.
      if (room > 0) lines.push(line.slice(0, room));
      truncated = true;
      return false;
    }
    size += line.length + 1;
    lines.push(line);
    return true;
  };

  const walk = (node: unknown, indent: string, depth: number): void => {
    if (truncated) return;
    if (!isContainer(node)) {
      push(`${indent}${scalar(node)}`);
      return;
    }
    if (depth >= maxDepth || seen.has(node)) {
      push(`${indent}…`);
      return;
    }
    seen.add(node);
    const entries: [string | null, unknown][] = Array.isArray(node)
      ? node.map((item) => [null, item])
      : Object.entries(node);
    entries.slice(0, maxItems).forEach(([key, item]) => {
      const head = key === null ? `${indent}-` : `${indent}${label(key)}:`;
      if (isContainer(item)) {
        if (push(head)) walk(item, `${indent}  `, depth + 1);
      } else {
        push(`${head} ${scalar(item)}`);
      }
    });
    if (entries.length > maxItems) push(`${indent}… (+${entries.length - maxItems})`);
    seen.delete(node);
  };

  walk(value, '', 0);
  const text = lines.join('\n');
  return truncated ? `${text}\n${COPY.summaryTruncated}` : text;
}
